import {
  CompanyConfig,
  getActiveCompanyConfig,
} from "domain/company/companyConfig";

import { ATTACHMENT_VARIANTS } from "data/products/attachments";
import { Attachment, Line, Material } from "types/canvasTypes";

import { checkLineIntersection, getAllIntersections } from "./intersections";

const RIGHT_ANGLE = 90;
const RIGHT_ANGLE_TOLERANCE = 1;
const STRAIGHT_ANGLE_TOLERANCE = 1;

function normalizeAngleDifference(angleDifference: number): number {
  const normalized = Math.abs(angleDifference) % 180;
  return normalized > RIGHT_ANGLE ? 180 - normalized : normalized;
}

function isRightAngle(line1: Line, line2: Line): boolean {
  if (typeof line1.angle === "number" && typeof line2.angle === "number") {
    const normalizedDifference = normalizeAngleDifference(line1.angle - line2.angle);
    return Math.abs(normalizedDifference - RIGHT_ANGLE) <= RIGHT_ANGLE_TOLERANCE;
  }

  const firstLine = line1.parallelLine || line1;
  const secondLine = line2.parallelLine || line2;
  const intersectionAngle = checkLineIntersection(firstLine, secondLine).angle;
  return Math.abs(intersectionAngle - RIGHT_ANGLE) <= RIGHT_ANGLE_TOLERANCE;
}

function isStraightAngle(line1: Line, line2: Line): boolean {
  if (typeof line1.angle === "number" && typeof line2.angle === "number") {
    const normalizedDifference = normalizeAngleDifference(line1.angle - line2.angle);
    return normalizedDifference <= STRAIGHT_ANGLE_TOLERANCE;
  }

  const firstLine = line1.parallelLine || line1;
  const secondLine = line2.parallelLine || line2;
  const intersectionAngle = checkLineIntersection(firstLine, secondLine).angle;
  return Math.abs(intersectionAngle) <= STRAIGHT_ANGLE_TOLERANCE;
}

export function getAttachmentCost(
  lines: Line[],
  companyConfig: CompanyConfig = getActiveCompanyConfig()
): Attachment[] {
  const attachmentsMap = companyConfig.catalog.attachments || ATTACHMENT_VARIANTS;
  const intersections = getAllIntersections(
    lines,
    companyConfig.rules.pointEqualityThresholdPx
  );
  const attachments: Attachment[] = [];
  let pannel90Count = 0;
  let adjustableCount = 0;
  let wallAttachmentCount = 0;

  for (let i = 0; i < intersections.length; i++) {
    if (intersections[i].intersections.length === 0) {
      wallAttachmentCount += 2;
    } else if (intersections[i].intersections.length === 1) {
      wallAttachmentCount += 1;
      if (isRightAngle(intersections[i].line, intersections[i].intersections[0])) {
        pannel90Count += 1;
      } else if (isStraightAngle(intersections[i].line, intersections[i].intersections[0])) {
        // Straight joints are handled as 180 connectors in pricing residual logic.
      } else {
        adjustableCount += 1;
      }
    } else if (intersections[i].intersections.length === 2) {
      if (isRightAngle(intersections[i].line, intersections[i].intersections[0])) {
        pannel90Count += 1;
      } else if (isStraightAngle(intersections[i].line, intersections[i].intersections[0])) {
        // Straight joints are handled as 180 connectors in pricing residual logic.
      } else {
        adjustableCount += 1;
      }

      if (isRightAngle(intersections[i].line, intersections[i].intersections[1])) {
        pannel90Count += 1;
      } else if (isStraightAngle(intersections[i].line, intersections[i].intersections[1])) {
        // Straight joints are handled as 180 connectors in pricing residual logic.
      } else {
        adjustableCount += 1;
      }
    }
  }

  attachments.push({
    ...attachmentsMap[Material.AdjustableAttachement],
    count: adjustableCount / 2,
  });
  attachments.push({
    ...attachmentsMap[Material.Pannel90Attachemnt],
    count: pannel90Count / 2,
  });
  attachments.push({
    ...attachmentsMap[Material.WallAttachment],
    count: wallAttachmentCount,
  });

  return attachments;
}
